import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import {
  API_CALL_PAY_TIMEOUT,
  API_CALL_TIMEOUT,
  API_GET_PAYMENT_STATUS,
  API_PAYMENT_SELECTION,
  BLIK_AUTH_TIMEOUT,
  BLIK_CONFIRM_TIMEOUT,
  DEFAULT_LANGUAGE,
  IS_LOCAL_MODE,
  LANGUAGES,
} from "../Constants";
import axiosWithRetry from "../axios";
import { captureException } from "@sentry/react";
import ErrorHandler from "../components/ErrorHandler";
import CallWait from "../components/CallWait";
import { Button, Card, Col, Container, FormGroup, Row } from "reactstrap";
import LanguageSelector from "../components/LanguageSelector";
import Footer from "../components/Footer";

import RedpayLogo from "../assets/images/logos/redpay-small.png";
import Counter from "../components/Counter";
import { llog } from "../utils/LogUtils";

const BlikPaymentPage = () => {
  const { t, i18n } = useTranslation();
  const { transactionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const languageParam = searchParams.get("lang");
  const langFound = LANGUAGES.find((lang) => lang.lang === languageParam);
  const language = langFound ? languageParam : DEFAULT_LANGUAGE;
  const navigate = useNavigate();

  const setLanguage = (lang) => {
    searchParams.set("lang", lang);
    setSearchParams(searchParams);
  };

  const codeRef = useRef();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [blikResult, setBlikResult] = useState("ENTER_CODE");
  const [awaitCounter, setAwaitCounter] = useState(0);

  // Set focus to code input.
  useEffect(() => {
    codeRef?.current?.focus();
  }, []);

  const redirect2Confirm = () => {
    window.location.href = transactionStatus.merchantConfirmUrl;
  };

  const redirect2Failed = () => {
    if (transactionStatus.merchantFailedUrl === "") {
      window.location.href = transactionStatus.merchantConfirmUrl + "?error=true";
    } else {
      window.location.href = transactionStatus.merchantFailedUrl;
    }
  };

  const handleAuthorize = (e) => {
    e.preventDefault();

    setBlikResult("CODE_ENTERED");
    llog("Authorize Blik: " + code);

    const axios = axiosWithRetry(1);
    const abortController = new AbortController();
    const signal = abortController.signal;
    const t6code = code.replace(/-/g, "");

    const selectionData = {
      type: "BLIK",
      blikCode: t6code,
      // firstname: transactionData.payeeFirstname,
      // lastname: transactionData.payeeLastname,
      email: "archoner@wp.pl",
    };

    axios
      .post(`${API_PAYMENT_SELECTION}/${transactionId}/select`, selectionData, {
        signal,
        timeout: API_CALL_PAY_TIMEOUT,
      })
      .then((response) => {
        const result = response.data.result;
        if (result === "BAD_BLIK_CODE") {
          setBlikResult("BAD_CODE");
        } else if (result === "AWAIT_AUTH") {
          setBlikResult("AWAIT_AUTH");
        } else {
          setBlikResult("ERROR");
          llog("Unexpected result " + result + " - " + response.data.result);
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setError(error);
          llog(error);
          captureException(error);
        }
      });
  };

  // Effect to check transaction status periodically when we await async result from blik
  useEffect(() => {
    llog("Result:" + blikResult);
    if (blikResult === "AWAIT_AUTH") {
      setTimeout(() => {
        llog("awaitCounter: " + awaitCounter);
        setAwaitCounter(awaitCounter + 1);
      }, 1000);
      const axios = axiosWithRetry();
      const abortController = new AbortController();
      const signal = abortController.signal;
      axios
        .get(`${API_GET_PAYMENT_STATUS}/${transactionId}/status`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          setTransactionStatus(response.data);
          llog(response.data);
          if (response.data.status === "SUCCESS") {
            setBlikResult("AUTHORIZED");
          } else if (response.data.status === "FAILED") {
            setBlikResult("FAILED");
          }
          return response.data.status;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            llog(error);
            captureException(error);
          }
        });
    }
  }, [blikResult, awaitCounter, transactionId]);

  const handleTimeout = () => {
    llog("Blik Transaction Timeout");
    if (IS_LOCAL_MODE) {
      setBlikResult("AUTHORIZED"); // Just for tests
    } else {
      setBlikResult("TIMEOUT");
    }
  };

  const handleTryAgain = () => {
    setCode("");
    setBlikResult("ENTER_CODE");
  };

  const handleCancel = () => {
    navigate("/payment/" + transactionId);
  };

  // Check initial blik status
  useEffect(() => {
    i18n.changeLanguage(language);

    const axios = axiosWithRetry();
    const abortController = new AbortController();
    if (!transactionStatus) {
      setLoading(true);
      const signal = abortController.signal;
      axios
        .get(`${API_GET_PAYMENT_STATUS}/${transactionId}/status`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          setTransactionStatus(response.data);
          llog(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setError(error);
            llog(error);
            captureException(error);
            setLoading(false);
          }
        });
    }
    return () => {
      abortController.abort();
    };
  }, [transactionId, transactionStatus, i18n, language]);

  const codeSyntaxValid = (newCode) => {
    return newCode.replace("-", "").replace("_", "").length !== 6;
  };

  if (error) return <ErrorHandler message={"Error Code:" + error.status} description={t("error_500")} />;

  if (loading) return <CallWait message={t("payname.blik")} description={t("call_wait_desc")} />;

  return (
    <div className='app-wrapper bg-white min-vh-100'>
      <Container>
        <Row className='my-5 align-items-center'>
          <Col md='10' lg='8'>
            <div>
              <img src={RedpayLogo} alt='Redpay logo' className='mx-2 mb-2' width={200} />
              <strong>{t("welcome")}</strong>
            </div>
          </Col>
          <Col md='2' lg='4' className='text-right'>
            <LanguageSelector language={language} setLanguage={setLanguage} />
          </Col>
        </Row>
        <Row className='my-5' style={{ marginBottom: "auto" }}>
          <Col className='text-center'>
            {blikResult === "ENTER_CODE" && (
              <Card className='d-flex align-items-center justify-content-center flex-wrap'>
                <div className='mt-4 mb-4'>
                  <h2>{t("blik-title")}</h2>
                </div>
                <div className='mb-3'>
                  {t("blik-entercode-desc")}
                  <br />
                  {t("blik-entercode-desc2")}
                </div>
                <Row className='justify-content-center'>
                  <Col lg='12 text-center'>
                    <FormGroup className='mb-3 mt-3'>
                      <div className='d-flex justify-content-between'>
                        <label htmlFor='code' className='font-weight-bold'>
                          {t("blik-authorisation-code")}
                        </label>
                      </div>
                      <InputMask
                        className='text-center form-control form-control-md font-size-xxl'
                        placeholder={t("code-placeholder")}
                        id='code'
                        type='text'
                        mask='999-999'
                        inputRef={codeRef}
                        autoComplete='off'
                        onChange={(e) => setCode(e.target.value)}
                        value={code}
                        required
                        tabIndex='1'
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className='my-3'>
                  <Button color='neutral-dark' size='xl' className='mt-2 mb-4 mr-4' onClick={handleCancel}>
                    {t("blik-cancel")}
                  </Button>
                  <Button
                    color='danger'
                    size='xl'
                    className='mt-2 mb-4'
                    onClick={handleAuthorize}
                    disabled={codeSyntaxValid(code)}
                  >
                    {t("blik-authorize")}
                  </Button>
                </Row>
              </Card>
            )}
            {(blikResult === "CODE_ENTERED" || blikResult === "AWAIT_AUTH") && (
              <Card className='d-flex align-items-center justify-content-center flex-wrap'>
                <div className='mt-4 mb-4'>
                  <h2>{t("blik-title")}</h2>
                </div>
                <div className='mb-3'>
                  {t("blik-await-auth-desc")}
                  <br />
                  {t("blik-await-auth-desc2")}
                </div>
                {blikResult === "AWAIT_AUTH" && (
                  <Counter counterTimeout={BLIK_AUTH_TIMEOUT} onTimeout={handleTimeout} color='primary' />
                )}
                <Row className='my-3'>
                  <Button color='neutral-dark' size='xl' className='mt-2 mb-4' onClick={handleCancel}>
                    {t("blik-cancel")}
                  </Button>
                </Row>
              </Card>
            )}
            {(blikResult === "BAD_CODE" || blikResult === "TIMEOUT") && (
              <Card className='d-flex align-items-center justify-content-center flex-wrap'>
                <div className='mt-4 mb-4'>
                  <h2>{t("blik-title")}</h2>
                </div>
                <div className='mb-3'>
                  {blikResult === "BAD_CODE" && t("blik-bad-code")}
                  {blikResult === "TIMEOUT" && t("blik-timeout")}
                  <br />
                  {t("blik-try-again")}
                </div>
                <Row className='my-3'>
                  <Button color='neutral-dark' size='xl' className='mt-2 mb-4 mr-4' onClick={handleCancel}>
                    {t("blik-cancel")}
                  </Button>
                  <Button
                    color='danger'
                    size='xl'
                    className='mt-2 mb-4'
                    onClick={handleTryAgain}
                    disabled={codeSyntaxValid(code)}
                  >
                    {t("blik-again")}
                  </Button>
                </Row>
              </Card>
            )}
            {blikResult === "AUTHORIZED" && (
              <Card className='d-flex align-items-center justify-content-center flex-wrap'>
                <div className='mt-4 mb-4'>
                  <h2>{t("blik-title-accepted")}</h2>
                </div>
                <div className='mb-3'>
                  {t("blik-authorized-desc")}
                  <br />
                  {t("blik-authorized-desc2")}
                </div>
                <Counter counterTimeout={BLIK_CONFIRM_TIMEOUT} onTimeout={redirect2Confirm} />
                <Row className='my-3'>
                  <Button color='neutral-danger' size='xl' className='mt-2 mb-4' onClick={redirect2Confirm}>
                    {t("blik-return")}
                  </Button>
                </Row>
              </Card>
            )}
            {blikResult === "FAILED" && (
              <Card className='d-flex align-items-center justify-content-center flex-wrap'>
                <div className='mt-4 mb-4'>
                  <h2>{t("blik-title-failed")}</h2>
                </div>
                <div className='mb-3'>
                  {t("blik-failed-desc")}
                  <br />
                  {t("blik-failed-desc2")}
                </div>
                <Counter counterTimeout={BLIK_CONFIRM_TIMEOUT} onTimeout={redirect2Confirm} color='dark' />
                <Row className='my-3'>
                  <Button color='neutral-danger' size='xl' className='mt-2 mb-4' onClick={redirect2Failed}>
                    {t("blik-return")}
                  </Button>
                </Row>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
};

export default BlikPaymentPage;
